import React, { useState, useEffect } from 'react';
import {Pie} from 'react-chartjs-2';
import ApiService from '../../services/apiService';
import { useOktaAuth } from '@okta/okta-react';
import { CHART_COLOR_PALETTE, CHART_COLOR_PALETTE_DARK } from './shared/chartColors';
import { CHART_DEFAULTS, CHART_LEGEND_DEFAULTS, CHART_TITLES } from './shared/chartConstants';
import { generateColors } from './shared/chartUtils';
import { createBaseChartOptions, createContainerStyles } from './shared/chartConfigFactory';
import { useNavigate } from 'react-router-dom';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useDarkTheme } from '../../contexts/DarkThemeContext';

    const ApiByStructure: React.FC = () => {
    const [apiTypeCounts, setapiTypeCounts] = React.useState<Map<string, number>>(new Map());
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const { isDarkTheme } = useDarkTheme();
    const [apiData, setApiData] = React.useState<any[]>([]);
    const [chartData, setChartData] = React.useState<number[]>([]);
    const [chartLabels, setChartLabels] = React.useState<string[]>([]);
    useWindowDimensions();

    // Click handler for pie chart segments
    const handleChartClick = (event: any, elements: any[]) => {
        if (elements && elements.length > 0) {
            const clickedElementIndex = elements[0].index;
            const clickedApiType = chartLabels[clickedElementIndex];
            
            // Navigate to table view with apiStructure filter
            const searchParams = new URLSearchParams();
            searchParams.set('apiStructure', clickedApiType);
            navigate(`/table-view?${searchParams.toString()}`);
        }
    };

    const extractData = (apiData: any[]) => {
        console.log("Extracting data from:", apiData);
        
        //Add a property name and return the processed data
        const processedData = apiData.map((api) => {
            const apiType = api.apiStructure && api.apiStructure.trim() !== '' 
                ? api.apiStructure 
                : 'Type Undefined';            
            return { apiType };
        });
        
        console.log("Extraction complete, processed data:", processedData);
        return processedData;
    }

    function countType<B>(array: B[], keyExtractor: (item: B) => string): Map<string, number> {
        const counts = new Map<string, number>();
        array.forEach(item => {
            const key = keyExtractor(item);
            // Count all types including "Type Undefined"
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        
        return counts;
    }

    const processMapData = (countsMap: Map<string, number>) => {
        console.log("Processing map data for chart:", countsMap);
        const labels: string[] = [];
        const values: number[] = [];
        
        // Convert Map entries to arrays for the chart
        countsMap.forEach((count, apiType) => {
            // Include all types, including "Type Undefined"
            labels.push(apiType);
            values.push(count);
        });
        
        setChartLabels(labels);
        setChartData(values);        
    }

    const getData = async() => {
        try {
            const apiService = new ApiService(); // Create an instance
            const data = (await apiService.getAllApis(authState)) as any[];
            setApiData(data);
            
            const processed = extractData(data);
            const counts = countType(processed, (item) => item.apiType);
            setapiTypeCounts(counts);
            processMapData(counts);
        } catch (error) {
            console.error("Error fetching API data for the Graphics:", error);
        } 
    }

    useEffect(() => {
        getData();
    }, [authState]);

    useEffect(() => {
        if (apiTypeCounts.size > 0) {
            processMapData(apiTypeCounts);
        }
    }, [apiTypeCounts]);

    // Use shared chart configuration with customized legend padding for multiple items
    const options = createBaseChartOptions({
        title: CHART_TITLES.API_BY_STRUCTURE,
        chartLabels: chartLabels,
        customLegendConfig: {
            padding: CHART_LEGEND_DEFAULTS.PADDING_COMPACT  // Smaller padding for ApiByStructure due to more legend items
        },
        onClick: handleChartClick,
        isDark: isDarkTheme
    });

    const labels = chartLabels;
    
    const palette = isDarkTheme ? CHART_COLOR_PALETTE_DARK : CHART_COLOR_PALETTE;

    const data = {
        labels,
        datasets: [
            {
                data: chartData,
                backgroundColor: generateColors(chartData.length, palette.BACKGROUND),
                borderColor: generateColors(chartData.length, palette.BORDER),
                borderWidth: CHART_DEFAULTS.BORDER_WIDTH,
            },
        ],
    }

    return (
        <div className="graphic-content chart-with-title-spacing" style={createContainerStyles()}>
            <Pie options={options} data={data} />
        </div>
    )

}

export default ApiByStructure;
